import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Badge,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Tooltip
} from '@mui/material';
import {
  DateRange as DateRangeIcon,
  Today as TodayIcon,
  CalendarMonth as CalendarMonthIcon,
  Lock as LockIcon
} from '@mui/icons-material';

const AnalysisPeriodSelector = ({ 
  dateRange, 
  onDateRangeChange, 
  selectedProduct, 
  selectedClient,
  getCurrentPeriodType,
  formatSelectedPeriod,
  isCurrentMonthSelected,
  disabled = false
}) => {
  const [showCustomPeriod, setShowCustomPeriod] = useState(false);
  const [customStartYear, setCustomStartYear] = useState(new Date().getFullYear());
  const [customStartMonth, setCustomStartMonth] = useState(new Date().getMonth() + 1);
  const [customEndYear, setCustomEndYear] = useState(new Date().getFullYear());
  const [customEndMonth, setCustomEndMonth] = useState(new Date().getMonth() + 1);

  // 年の選択肢を生成（現在年から過去5年）
  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i <= 5; i++) {
      years.push(currentYear - i);
    }
    return years;
  };

  // 月の選択肢
  const monthOptions = [
    { value: 1, label: '1月' },
    { value: 2, label: '2月' },
    { value: 3, label: '3月' },
    { value: 4, label: '4月' },
    { value: 5, label: '5月' },
    { value: 6, label: '6月' },
    { value: 7, label: '7月' },
    { value: 8, label: '8月' },
    { value: 9, label: '9月' },
    { value: 10, label: '10月' },
    { value: 11, label: '11月' },
    { value: 12, label: '12月' }
  ];

  // 期間プリセット処理
  const handlePeriodPreset = (type) => {
    if (disabled && type !== 'thisMonth') {
      return;
    }

    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    
    switch (type) {
      case 'thisMonth':
        const currentMonth = String(month + 1).padStart(2, '0');
        onDateRangeChange({
          startDate: `${year}-${currentMonth}-01`,
          endDate: `${year}-${currentMonth}-${new Date(year, month + 1, 0).getDate()}`
        });
        break;
      case 'lastMonth':
        const lastMonth = month === 0 ? 11 : month - 1;
        const lastMonthYear = month === 0 ? year - 1 : year;
        const lastMonthStr = String(lastMonth + 1).padStart(2, '0');
        onDateRangeChange({
          startDate: `${lastMonthYear}-${lastMonthStr}-01`,
          endDate: `${lastMonthYear}-${lastMonthStr}-${new Date(lastMonthYear, lastMonth + 1, 0).getDate()}`
        });
        break;
      case 'last3Months':
        const threeMonthsAgo = new Date(year, month - 2, 1);
        const currentMonthStr = String(month + 1).padStart(2, '0');
        onDateRangeChange({
          startDate: `${threeMonthsAgo.getFullYear()}-${String(threeMonthsAgo.getMonth() + 1).padStart(2, '0')}-01`,
          endDate: `${year}-${currentMonthStr}-${new Date(year, month + 1, 0).getDate()}`
        });
        break;
      case 'last6Months':
        const sixMonthsAgo = new Date(year, month - 5, 1);
        const currentMonthStr2 = String(month + 1).padStart(2, '0');
        onDateRangeChange({
          startDate: `${sixMonthsAgo.getFullYear()}-${String(sixMonthsAgo.getMonth() + 1).padStart(2, '0')}-01`,
          endDate: `${year}-${currentMonthStr2}-${new Date(year, month + 1, 0).getDate()}`
        });
        break;
      case 'last12Months':
        const twelveMonthsAgo = new Date(year, month - 11, 1);
        const currentMonthStr3 = String(month + 1).padStart(2, '0');
        onDateRangeChange({
          startDate: `${twelveMonthsAgo.getFullYear()}-${String(twelveMonthsAgo.getMonth() + 1).padStart(2, '0')}-01`,
          endDate: `${year}-${currentMonthStr3}-${new Date(year, month + 1, 0).getDate()}`
        });
        break;
      default:
        break;
    }
    setShowCustomPeriod(false);
  };

  // カスタム期間適用
  const applyCustomPeriod = () => {
    if (disabled) return;

    // 月数計算
    const startDate = new Date(customStartYear, customStartMonth - 1);
    const endDate = new Date(customEndYear, customEndMonth - 1);
    const monthDiff = (endDate.getFullYear() - startDate.getFullYear()) * 12 + 
                     (endDate.getMonth() - startDate.getMonth()) + 1;

    // 最大12ヶ月チェック
    if (monthDiff > 12) {
      alert('最大12ヶ月まで選択可能です。期間を短縮してください。');
      return;
    }

    if (startDate > endDate) {
      alert('開始月は終了月より前である必要があります。');
      return;
    }

    const startDateStr = `${customStartYear}-${String(customStartMonth).padStart(2, '0')}-01`;
    const endDateStr = `${customEndYear}-${String(customEndMonth).padStart(2, '0')}-${new Date(customEndYear, customEndMonth, 0).getDate()}`;
    
    onDateRangeChange({
      startDate: startDateStr,
      endDate: endDateStr
    });
    setShowCustomPeriod(false);
  };

  // ボタンのレンダリング関数（修正版）
  const renderPeriodButton = (type, label, icon = null, isSelected = false) => {
    const isRestricted = disabled && type !== 'thisMonth';
    
    const button = (
      <Button
        variant={isSelected ? "contained" : "outlined"}
        size="small"
        startIcon={icon}
        onClick={() => handlePeriodPreset(type)}
        disabled={isRestricted}
        sx={{ 
          fontWeight: isSelected ? 600 : 400,
          position: 'relative',
          minWidth: 'auto',
          whiteSpace: 'nowrap'
        }}
      >
        {label}
        {isRestricted && <LockIcon sx={{ ml: 1, fontSize: 14 }} />}
      </Button>
    );

    if (isRestricted) {
      return (
        <Tooltip title="スタンダードプラン以上で利用可能" arrow>
          <span>{button}</span>
        </Tooltip>
      );
    }

    return button;
  };

  return (
    <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <DateRangeIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            分析期間
          </Typography>
          {disabled && (
            <Tooltip title="フリープランでは今月のみ表示可能">
              <LockIcon fontSize="small" color="warning" />
            </Tooltip>
          )}
        </Box>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              期間プリセット
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {renderPeriodButton('thisMonth', '今月', <TodayIcon />, getCurrentPeriodType() === 'thisMonth')}
              {renderPeriodButton('lastMonth', '先月', null, getCurrentPeriodType() === 'lastMonth')}
              {renderPeriodButton('last3Months', '過去3ヶ月')}
              {renderPeriodButton('last6Months', '過去6ヶ月')}
              {renderPeriodButton('last12Months', '過去12ヶ月')}
            </Box>
            
            {/* カスタム期間ボタン */}
            <Box sx={{ mt: 2 }}>
              <Tooltip title={disabled ? "スタンダードプラン以上で利用可能" : ""} arrow>
                <span>
                  <Button
                    variant={showCustomPeriod ? "contained" : "outlined"}
                    size="small"
                    color={showCustomPeriod ? "secondary" : "primary"}
                    onClick={() => !disabled && setShowCustomPeriod(!showCustomPeriod)}
                    disabled={disabled}
                  >
                    カスタム期間
                    {disabled && <LockIcon sx={{ ml: 1, fontSize: 16 }} />}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              {showCustomPeriod ? 'カスタム期間設定' : '選択中の期間'}
            </Typography>
            
            {/* カスタム期間設定時は、この場所に設定UIを表示 */}
            {showCustomPeriod && !disabled ? (
              <Box sx={{ 
                p: 3, 
                border: '1px solid', 
                borderColor: 'secondary.light', 
                borderRadius: 2,
                bgcolor: 'grey.50'
              }}>
                <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
                  期間を選択（最大12ヶ月）
                </Typography>
                
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>開始年</InputLabel>
                      <Select
                        value={customStartYear}
                        onChange={(e) => setCustomStartYear(e.target.value)}
                        label="開始年"
                      >
                        {generateYearOptions().map(year => (
                          <MenuItem key={year} value={year}>{year}年</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>開始月</InputLabel>
                      <Select
                        value={customStartMonth}
                        onChange={(e) => setCustomStartMonth(e.target.value)}
                        label="開始月"
                      >
                        {monthOptions.map(month => (
                          <MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>終了年</InputLabel>
                      <Select
                        value={customEndYear}
                        onChange={(e) => setCustomEndYear(e.target.value)}
                        label="終了年"
                      >
                        {generateYearOptions().map(year => (
                          <MenuItem key={year} value={year}>{year}年</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>終了月</InputLabel>
                      <Select
                        value={customEndMonth}
                        onChange={(e) => setCustomEndMonth(e.target.value)}
                        label="終了月"
                      >
                        {monthOptions.map(month => (
                          <MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    onClick={() => setShowCustomPeriod(false)}
                  >
                    キャンセル
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={applyCustomPeriod}
                  >
                    期間を適用
                  </Button>
                </Box>
                
                <Alert severity="info" sx={{ mt: 2 }}>
                  月単位での期間選択です。最大12ヶ月まで選択可能。
                </Alert>
              </Box>
            ) : (
              /* 通常時は選択中期間を表示 */
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                p: 3, 
                bgcolor: 'primary.50', 
                borderRadius: 2,
                border: '2px solid',
                borderColor: 'primary.main'
              }}>
                <CalendarMonthIcon color="primary" sx={{ fontSize: 32 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    📅 {formatSelectedPeriod()}
                  </Typography>
                  {selectedProduct && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                      製品: {selectedProduct.name} {selectedClient && `| 顧客: ${selectedClient.name}`}
                    </Typography>
                  )}
                </Box>
                {isCurrentMonthSelected() && (
                  <Chip 
                    label="現在月" 
                    size="small" 
                    color="primary" 
                    variant="filled"
                  />
                )}
              </Box>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default AnalysisPeriodSelector;